// import components
import { IconButton, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { DrawerContext } from '../Drawer/DrawerContext';
import DrawerFooter from '../Drawer/DrawerFooter';

// import context
import { MultiStepFormContext, useMultiStepFormContext } from './context';

// import enums
import { Status } from './MultiStepForm.enums';

// import external libraries
import { path } from 'ramda';
import { useContext, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';

// import styles
import {
  StyledFormSectionMainBox,
  StyledMultiStepForm,
  StyledSection,
  StyledShowPreviewBox,
  StyledShowPreviewLeftBox
} from './MultiStepForm.styles';

// import types
import type { FC } from 'react';
import type {
  MultiStepFormProps,
  MultiStepFormSectionPreviewProps,
  MultiStepFormSectionProps
} from './MultiStepForm.types';

const PREFIX = 'MultiStepForm';

const classes = {
  fixedHeight: `${PREFIX}-fixedHeight`,
  autoHeight: `${PREFIX}-autoHeight`
};

const MultiStepFormSectionPreview: FC<MultiStepFormSectionPreviewProps> = ({
  children,
  preview,
  isPreview,
  isEditable
}) => {
  'use memo';

  const {
    formState: { submitCount }
  } = useMultiStepFormContext();

  const [editing, setEditing] = useState<boolean>(isPreview);

  // Collapse back to the preview after every submit.
  useEffect(() => {
    setEditing(false);
  }, [submitCount]);

  function onEditClick() {
    setEditing(!editing);
  }

  return (
    <>
      {!editing ? (
        <StyledFormSectionMainBox>
          {preview}
          {isEditable && (
            <IconButton onClick={onEditClick} size="large">
              <Icon type="pen" />
            </IconButton>
          )}
        </StyledFormSectionMainBox>
      ) : (
        children
      )}
    </>
  );
};

const MultiStepFormSection: FC<MultiStepFormSectionProps> = ({
  children,
  title,
  validateFields = [],
  preview,
  isPreview = false,
  isEditable = true,
  isImportant = false,
  fullWidth = false,
  ...rest
}) => {
  'use memo';

  const {
    previewMode,
    formState: { errors },
    getFieldState,
    watch,
    getValues
  } = useMultiStepFormContext();

  // Load-bearing despite the discarded result: this flips RHF's watchAll, which
  // is what re-renders the form on every value change and so recomputes the
  // status below. Do not narrow it to useWatch({ name: validateFields }) without
  // checking the callers — a fair number of them render values straight from
  // getValues() and lean on the same re-render.
  watch();

  const isComplete = validateFields.every((field) => {
    const fieldState = getFieldState(field);
    const value = getValues(field);

    const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value);

    // Check if user has entered a value and if the value is valid
    return hasValue && !fieldState.invalid;
  });

  const hasError = validateFields.some((field) =>
    path(field?.split('.'), errors)
  );

  const status = isComplete
    ? Status.Success
    : hasError
    ? Status.Error
    : Status.Pending;

  const statusIcon = status === Status.Error ? 'circle-xmark' : 'circle-check';

  const showPreview = isPreview || previewMode;

  return (
    <StyledSection {...rest}>
      <StyledShowPreviewLeftBox fullWidth={fullWidth}>
        <Icon
          type={isImportant ? 'circle-exclamation' : statusIcon}
          color={isImportant ? Status.Warning : status}
        />
        <Typography variant="h6">{title}</Typography>
      </StyledShowPreviewLeftBox>
      <StyledShowPreviewBox>
        {!showPreview && isEditable ? (
          children
        ) : (
          <MultiStepFormSectionPreview
            preview={preview}
            isPreview={isPreview}
            isEditable={isEditable}
          >
            {children}
          </MultiStepFormSectionPreview>
        )}
      </StyledShowPreviewBox>
    </StyledSection>
  );
};

function MultiStepForm({
  children,
  methods,
  onSubmit,
  fixedHeight = true,
  previewMode = false,
  fullHeight = true,
  style
}: MultiStepFormProps) {
  'use memo';

  const { onInnerFormChange } = useContext(DrawerContext);
  const { isDirty } = methods.formState;

  // Only isDirty is reported, so nothing here depends on the `methods` identity
  // — which not every caller keeps stable across renders.
  useEffect(() => {
    onInnerFormChange?.(isDirty);
  }, [isDirty, onInnerFormChange]);

  return (
    <MultiStepFormContext.Provider value={{ previewMode }}>
      <FormProvider {...methods}>
        <StyledMultiStepForm
          className={fixedHeight ? classes.fixedHeight : classes.autoHeight}
          onSubmit={methods.handleSubmit(onSubmit)}
          noValidate
          fullHeight={fullHeight}
          {...(style ? { style } : {})}
        >
          {children}
        </StyledMultiStepForm>
      </FormProvider>
    </MultiStepFormContext.Provider>
  );
}

MultiStepForm.Section = MultiStepFormSection;
MultiStepForm.SectionPreview = MultiStepFormSectionPreview;
MultiStepForm.Actions = DrawerFooter;

export default MultiStepForm;
